import { KText } from '@/ui-library/components/text';
import {
  Flex,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@chakra-ui/react';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { InterviewType } from '../types/interview';
import { InterviewNotes } from './InterviewNotes';
import {
  ScorecardTabWrapper,
  ScorecardVisibilityMode,
} from '../scorecard/scorecard-visibility';
import { InterviewResume } from './InterviewResume';
import { InterviewContact } from './InterviewContact';
import { InterviewSummary } from './InterviewSummary';
import { InterviewTranscript } from '../interview-transcript';
import { useSelector } from 'react-redux';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { GroupedScorecards } from '../scorecard/scorecard-visibility/types';
import { useKFlags } from '@/hooks/use-k-flags';
import { AllScorecardTabWrapper } from '../scorecard/scorecard-visibility/AllScoreCards/AllScorecardTabWrapper';
import { hasUserSubmittedScorecard } from '../scorecard/scorecard-visibility';
import {
  getCurrentUserScorecard,
  isPrivilegedScorecardViewer,
} from '../scorecard/scorecard-visibility/helpers';

export type TabKey =
  | 'summary'
  | 'recording'
  | 'scorecard'
  | 'resume'
  | 'contact'
  | 'notes'
  | 'all_scorecards';

export const InterviewTabs = memo(function InterviewTabs({
  interview,
  tabs,
  interviewId,
  scorecardVisibilityMode,
  apiGroupedScorecards,
  refetchScorecardVisibility = () => {},
}: {
  interview: InterviewType;
  tabs: TabKey[];
  interviewId?: string | undefined | number;
  scorecardVisibilityMode?: ScorecardVisibilityMode;
  apiGroupedScorecards?: GroupedScorecards;
  /** No-op when tabs omit scorecard views (e.g. transcript-only drawer). */
  refetchScorecardVisibility?: () => void;
}) {
  const [currentTab, setCurrentTab] = useState<TabKey>(tabs[0]);
  const isResumeAvailable = Boolean(interview?.candidate?.resume?.url);
  const currUser = useSelector(selectCurrUser);
  const { feScorecardVisibility } = useKFlags();

  // Calculate if current user has submitted their scorecard
  const hasCurrentUserSubmitted = useMemo(() => {
    if (!apiGroupedScorecards?.currentInterview || !currUser?.id) return false;
    return hasUserSubmittedScorecard(
      apiGroupedScorecards?.currentInterview,
      currUser?.id
    );
  }, [apiGroupedScorecards?.currentInterview, currUser?.id]);

  const shouldShowAllScorecardsTab = useMemo(() => {
    if (!feScorecardVisibility) return false;
    const historyScorecards = apiGroupedScorecards?.history ?? [];
    const nonCurrUserScorecards =
      apiGroupedScorecards?.currentInterview?.filter(
        (sc) => sc.reviewer.id !== currUser?.id && sc.status === 'submitted'
      ) ?? [];
    const currentUserScorecard = getCurrentUserScorecard(
      apiGroupedScorecards?.currentInterview ?? [],
      currUser?.id
    );
    const hasCurrentUserScorecard = !!currentUserScorecard;

    // Privileged viewers (global admins/super-admins or the job's hiring team)
    // already have access to every scorecard, so they always see the tab when
    // any exist — bypassing both the visibility setting and the bias gate that
    // hides others' scorecards until you've filled your own.
    if (
      isPrivilegedScorecardViewer(interview, currUser, hasCurrentUserScorecard)
    ) {
      return nonCurrUserScorecards.length > 0 || historyScorecards.length > 0;
    }

    if (scorecardVisibilityMode === 'never') {
      // const currentUserScorecard = getCurrentUserScorecard(
      //   apiGroupedScorecards?.currentInterview ?? [],
      //   currUser?.id
      // );
      // const hasCurrentUserScorecard = !!currentUserScorecard;
      // const isOneOnOneInterview =
      //   interview?.kind === 'one_on_one' ||
      //   interview?.participants?.length === 1;

      // (admin || HM) && noCurrUserScorecard && otherScorecards => dont show all_scorecards
      if (
        interview?.can_edit &&
        !hasCurrentUserScorecard &&
        nonCurrUserScorecards.length > 0
      ) {
        return false;
      }

      if (
        interview?.can_edit
        // !isOneOnOneInterview
        // hasCurrentUserScorecard
      ) {
        return nonCurrUserScorecards.length > 0 || historyScorecards.length > 0;
      }
      return false;
    }

    // here, we might need to check if the current user has a scorecard
    // because if he doesn't, then an empty all_scorecards tab will be shown
    if (scorecardVisibilityMode === 'always') {
      // (admin || HM) && noCurrUserScorecard && otherScorecards => dont show all_scorecards
      if (
        interview?.can_edit &&
        !hasCurrentUserScorecard &&
        nonCurrUserScorecards.length > 0
      ) {
        return false;
      }

      return nonCurrUserScorecards.length > 0 || historyScorecards.length > 0;
    }

    if (scorecardVisibilityMode === 'after_submission') {
      // (admin || HM) && noCurrUserScorecard && otherScorecards => dont show all_scorecards
      if (
        interview?.can_edit &&
        !hasCurrentUserScorecard &&
        nonCurrUserScorecards.length > 0
      ) {
        return false;
      }

      return (
        (nonCurrUserScorecards.length > 0 || historyScorecards.length > 0) &&
        (hasCurrentUserSubmitted || interview?.can_edit)
      );
    }
  }, [
    scorecardVisibilityMode,
    hasCurrentUserSubmitted,
    feScorecardVisibility,
    apiGroupedScorecards,
    interview,
    currUser,
  ]);

  const TAB_DEFINITIONS_MAP = useMemo(
    () => ({
      resume: {
        id: 'resume' as const,
        label: 'Resume',
        component: InterviewResume,
      },
      contact: {
        id: 'contact' as const,
        label: 'Contact info',
        component: InterviewContact,
      },
      scorecard: {
        id: 'scorecard' as const,
        label: 'Scorecard',
        component: ScorecardTabWrapper,
      },
      all_scorecards: {
        id: 'all_scorecards' as const,
        label: 'All scorecards',
        component: AllScorecardTabWrapper,
        showTab: shouldShowAllScorecardsTab,
      },
      notes: {
        id: 'notes' as const,
        label: 'Notes',
        component: InterviewNotes,
      },
      summary: {
        id: 'summary' as const,
        label: 'Summary',
        component: InterviewSummary,
      },
      recording: {
        id: 'recording' as const,
        label: 'Recording',
        component: InterviewTranscript,
      },
    }),
    [shouldShowAllScorecardsTab]
  );

  const commonProps = useMemo(
    () => ({ interview, interviewId, scorecardVisibilityMode }),
    [interview, interviewId, scorecardVisibilityMode]
  );

  const scorecardTabCommonProps = useMemo(
    () => ({
      ...commonProps,
      apiGroupedScorecards,
      hasCurrentUserSubmitted,
      interview,
      scorecardVisibilityMode,
      refetchScorecardVisibility,
    }),
    [
      commonProps,
      apiGroupedScorecards,
      hasCurrentUserSubmitted,
      interview,
      scorecardVisibilityMode,
      refetchScorecardVisibility,
    ]
  );

  const scorecardTabProps = useMemo(
    () => ({ ...scorecardTabCommonProps, tabId: 'scorecard' as const }),
    [scorecardTabCommonProps]
  );

  const allScorecardsTabProps = useMemo(
    () => ({ ...scorecardTabCommonProps, tabId: 'all_scorecards' as const }),
    [scorecardTabCommonProps]
  );

  const orderedTabs = useMemo(() => {
    return tabs
      .map((key) => {
        if (key === 'resume') {
          const def = isResumeAvailable
            ? TAB_DEFINITIONS_MAP.resume
            : TAB_DEFINITIONS_MAP.contact;
          return { ...def, props: commonProps, showTab: true };
        }
        const def = TAB_DEFINITIONS_MAP[key];
        if (!def) return null;
        const showTab =
          'showTab' in def && def.showTab !== undefined ? def.showTab : true;
        return { ...def, props: commonProps, showTab };
      })
      .filter(Boolean)
      .filter((t) => t.showTab !== false) as Array<{
      id: TabKey;
      label: string;
      component: React.ComponentType<unknown>;
      props: typeof commonProps;
      showTab?: boolean;
    }>;
  }, [tabs, isResumeAvailable, commonProps, TAB_DEFINITIONS_MAP]);

  const tabIndex = useMemo(() => {
    const index = orderedTabs.findIndex((t) => t.id === currentTab);
    return index >= 0 ? index : 0;
  }, [orderedTabs, currentTab]);

  const handleTabClick = useCallback((id: TabKey) => setCurrentTab(id), []);

  return (
    <Flex flex="1" maxH="100%" overflowY="hidden">
      <Tabs
        display="flex"
        flexDir="column"
        flex="1"
        h="100%"
        alignItems="stretch"
        index={tabIndex}
        isLazy
        overflow="hidden"
      >
        <TabList sx={{ borderBottomWidth: '1px' }}>
          {orderedTabs.map((option) => (
            <Tab
              key={option.id}
              _active={{ boxShadow: '' }}
              _focus={{ boxShadow: '' }}
              _selected={{ color: 'ats-primary', borderColor: 'ats-primary' }}
              onClick={() => handleTabClick(option.id as TabKey)}
              alignItems="center"
              mx="lg"
              pt="4px"
              pb="12px"
              px="0px"
              minH="38px"
              mt="12px"
            >
              <KText color="inherit" size="sm-medium">
                {option.label}
              </KText>
            </Tab>
          ))}
        </TabList>

        <TabPanels h="100%" overflowY="hidden">
          {orderedTabs.map((option) => {
            const Component = option.component;
            return (
              <TabPanel
                p="0"
                key={option.id}
                h="100%"
                overflowY="hidden"
                flex="1"
              >
                {option.id === 'scorecard' ? (
                  <Component
                    {...(scorecardTabProps as Record<string, unknown>)}
                  />
                ) : option.id === 'all_scorecards' ? (
                  <Component
                    {...(allScorecardsTabProps as Record<string, unknown>)}
                  />
                ) : (
                  <Component {...(option.props as Record<string, unknown>)} />
                )}
              </TabPanel>
            );
          })}
        </TabPanels>
      </Tabs>
    </Flex>
  );
});
